import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: true,
});

function renderBlockActions() {
  return (
    `<span class="code-block-actions">` +
    `<span class="code-block-action" data-action="send-code" title="Generate image"><i class="mdi mdi-auto-fix"></i></span>` +
    `<span class="code-block-action" data-action="copy-code" title="Copy"><i class="mdi mdi-content-copy"></i></span>` +
    `</span>`
  );
}

function renderBlockHeader(label) {
  return (
    `<div class="code-block-header">` +
    `<span class="code-block-lang">${label}</span>` +
    renderBlockActions() +
    `</div>`
  );
}

function wrapWithActionBar(label, inner) {
  return `<div class="code-block-wrapper">${renderBlockHeader(label)}${inner}</div>`;
}

// Wrap fenced code blocks with an action bar (Copy / Send to Image Gen).
const defaultFence = md.renderer.rules.fence;
md.renderer.rules.fence = function(tokens, idx, options, env, self) {
  const token = tokens[idx];
  const lang = token.info ? md.utils.escapeHtml(token.info.trim().split(/\s+/)[0]) : '';
  const inner = defaultFence(tokens, idx, options, env, self);
  return wrapWithActionBar(lang || 'Prompt', inner);
};

// Wrap blockquotes (> …) with the same action bar.
const defaultBlockquoteOpen =
  md.renderer.rules.blockquote_open ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };
const defaultBlockquoteClose =
  md.renderer.rules.blockquote_close ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.blockquote_open = function(tokens, idx, options, env, self) {
  return (
    `<div class="code-block-wrapper">` +
    renderBlockHeader('Prompt') +
    defaultBlockquoteOpen(tokens, idx, options, env, self)
  );
};

md.renderer.rules.blockquote_close = function(tokens, idx, options, env, self) {
  return defaultBlockquoteClose(tokens, idx, options, env, self) + `</div>`;
};

// Wide tables scroll horizontally instead of squeezing columns.
const defaultTableOpen =
  md.renderer.rules.table_open ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };
const defaultTableClose =
  md.renderer.rules.table_close ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.table_open = function(tokens, idx, options, env, self) {
  return `<div class="markdown-table-wrap">${defaultTableOpen(tokens, idx, options, env, self)}`;
};

md.renderer.rules.table_close = function(tokens, idx, options, env, self) {
  return `${defaultTableClose(tokens, idx, options, env, self)}</div>`;
};

// Ensure outbound links are safe when rendered via v-html.
const defaultLinkOpen =
  md.renderer.rules.link_open ||
  function(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function(tokens, idx, options, env, self) {
  const token = tokens[idx];
  const targetIndex = token.attrIndex('target');
  const relIndex = token.attrIndex('rel');

  if (targetIndex < 0) token.attrPush(['target', '_blank']);
  else token.attrs[targetIndex][1] = '_blank';

  const relValue = 'noopener noreferrer nofollow';
  if (relIndex < 0) token.attrPush(['rel', relValue]);
  else token.attrs[relIndex][1] = relValue;

  return defaultLinkOpen(tokens, idx, options, env, self);
};

export function renderMarkdown(input) {
  const raw = input != null ? String(input) : '';
  if (!raw.trim()) return '';

  const rendered = md.render(raw);

  return DOMPurify.sanitize(rendered, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['data-action'],
  });
}
